import axios from './axiosService';

/**
 * Concurrent requests
 * @see https://chatgpt.com/c/21242e9d-a39d-4995-8f7b-b092bf75d4f1
 */
export default {
  async testFetching(url) {
    console.log(`Testing ${url}`);
    const tests = [
      {chunkSize: 100, concurrentRequests: 1},
      {chunkSize: 100, concurrentRequests: 2},
      {chunkSize: 100, concurrentRequests: 3},
      {chunkSize: 100, concurrentRequests: 4},
      {chunkSize: 100, concurrentRequests: 5},
      {chunkSize: 200, concurrentRequests: 1},
      {chunkSize: 200, concurrentRequests: 2},
      {chunkSize: 200, concurrentRequests: 3},
      {chunkSize: 200, concurrentRequests: 4},
      {chunkSize: 200, concurrentRequests: 5},
      {chunkSize: 300, concurrentRequests: 1},
      {chunkSize: 300, concurrentRequests: 2},
      {chunkSize: 300, concurrentRequests: 3},
      {chunkSize: 300, concurrentRequests: 4},
      {chunkSize: 300, concurrentRequests: 5},
      {chunkSize: 400, concurrentRequests: 1},
      {chunkSize: 400, concurrentRequests: 2},
      {chunkSize: 400, concurrentRequests: 3},
      {chunkSize: 400, concurrentRequests: 4},
      {chunkSize: 400, concurrentRequests: 5}
    ];
    let fastestTime = 1_000_000;
    let fastestLabel = null;
    for (const test of tests) {
      for (let time = 0; time < 3; time++) {
        const label = `chunkSize = ${test.chunkSize} concurrentRequests = ${test.concurrentRequests}`;
        const startTime = performance.now();
        await this.fetchRecords(url, {}, test.chunkSize, test.concurrentRequests)
        const endTime = performance.now();
        const executionTime = endTime - startTime;
        console.log(`${label} = ${executionTime}`)
        if (executionTime < fastestTime) {
          fastestTime = executionTime;
          fastestLabel = label;
        }
      }
    }
    console.log(`Best test: ${fastestLabel} - ${fastestTime}`);
    return [];
  },
  /**
   * Fetches records from a given URL in chunks with support for concurrency and duplicate prevention.
   *
   * @param {string} url - The endpoint URL from which records are to be fetched.
   * @param {Object} [params={}] - Additional query parameters to include in the request.
   * @param {number} [chunkSize=1000] - The number of records to fetch in a single request.
   * @param {number} [concurrentRequests=3] - The number of concurrent requests to execute for fetching chunks.
   * @return {Promise<Array>} A promise that resolves to an array of all the fetched records.
   */
  async fetchRecords(url, params = {}, chunkSize = 1_000, concurrentRequests = 3) {
    let minID = 0;
    const allRecords = [];
    const recordIDs = new Set(); // track the IDs of the records to prevent duplicates from being retrieved.

    const initialConfig = {
      params: {
        ...params,
        min: 0,
        chunk: chunkSize,
      }
    }

    try {
      // MLD-7498
      const initialBatch = await axios.get(url, initialConfig);
      if (initialBatch.data.length < chunkSize) {
        console.log(`Fetched ${initialBatch.data.length} records from ${url} in one batch.`);
        return Promise.resolve(initialBatch.data);
      }
      minID = initialBatch.data[initialBatch.data.length - 1].id + 1;
      allRecords.push(...initialBatch.data);

      const fetchChunk = async (start, chunkSize) => {
        const config = {
          params: {
            ...params,
            min: start,
            chunk: chunkSize,
          }
        };
        const {data} = await axios.get(url, config);
        return data;
      };

      while (true) {
        const promises = [];
        for (let i = 0; i < concurrentRequests; i++) {
          promises.push(fetchChunk(minID + (i * chunkSize), chunkSize));
        }

        const batches = await Promise.all(promises);
        let hasMoreData = false;
        let maxFetchedID = minID;

        for (const batch of batches) {
          if (batch.length > 0) {
            hasMoreData = true;
            batch.forEach(record => {
              if (recordIDs.has(record.id) === false) {
                recordIDs.add(record.id);
                allRecords.push(record);
              }
            });
            maxFetchedID = Math.max(maxFetchedID, batch[batch.length - 1].id);
          }
        }

        if (!hasMoreData) break;

        minID = maxFetchedID + 1;
      }
    } catch (e) {
      console.log('fetchRecords exception:', e.message, url);
    }

    console.log(`Fetched ${allRecords.length} records from ${url}`);

    return Promise.resolve(allRecords);
  }
};
